import { useEffect, useRef } from "react";
import { CHAPTER_COUNT } from "../data/experience";
import { stepProductFocus } from "../lib/product-focus";
import { useExperienceStore } from "../store/experience-store";

interface ShowroomControlOptions {
  readonly scrollToChapter: (
    index: number,
    immediate?: boolean,
    duration?: number,
  ) => void;
  readonly scrollToEnd: (duration: number, onComplete: () => void) => void;
  readonly cancelProgrammaticScroll: () => void;
}

export function useShowroomControls({
  cancelProgrammaticScroll,
  scrollToChapter,
  scrollToEnd,
}: ShowroomControlOptions): void {
  const chapterIndex = useExperienceStore((store) => store.chapterIndex);
  const cameraSignal = useExperienceStore((store) => store.cameraSignal);
  const autoPlay = useExperienceStore((store) => store.autoPlay);
  const setAutoPlay = useExperienceStore((store) => store.setAutoPlay);
  const setHeroRotation = useExperienceStore((store) => store.setHeroRotation);
  const setExplosion = useExperienceStore((store) => store.setExplosion);
  const setFocusedProductIndex = useExperienceStore(
    (store) => store.setFocusedProductIndex,
  );
  const previousGestureRef = useRef(cameraSignal.gesture);
  const resumeTimerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const scheduleAutoPlayResume = (): void => {
      window.clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = window.setTimeout(() => setAutoPlay(true), 18_000);
    };

    const pauseAutoPlay = (): void => {
      if (useExperienceStore.getState().autoPlay) {
        cancelProgrammaticScroll();
        setAutoPlay(false);
      }
      scheduleAutoPlayResume();
    };

    const pauseAutoPlayForWheel = (): void => {
      if (useExperienceStore.getState().autoPlay) setAutoPlay(false);
      scheduleAutoPlayResume();
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      if (
        chapterIndex === 4 &&
        (event.key === "ArrowDown" || event.key === "ArrowUp")
      ) {
        event.preventDefault();
        pauseAutoPlay();
        const currentFocus =
          useExperienceStore.getState().focusedProductIndex;
        setFocusedProductIndex(
          stepProductFocus(
            currentFocus,
            event.key === "ArrowDown" ? 1 : -1,
          ),
        );
        return;
      }
      if (chapterIndex === 4 && event.key === "Escape") {
        pauseAutoPlay();
        setFocusedProductIndex(null);
        return;
      }
      if (event.key === "ArrowRight" || event.key === "PageDown") {
        pauseAutoPlay();
        scrollToChapter(chapterIndex + 1);
      }
      if (event.key === "ArrowLeft" || event.key === "PageUp") {
        pauseAutoPlay();
        scrollToChapter(chapterIndex - 1);
      }
      if (event.key === "Home") {
        pauseAutoPlay();
        scrollToChapter(0);
      }
      if (event.key === " ") {
        event.preventDefault();
        cancelProgrammaticScroll();
        window.clearTimeout(resumeTimerRef.current);
        setAutoPlay(!autoPlay);
      }
    };

    const onUserInput = (): void => pauseAutoPlay();
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("pointerdown", onUserInput, { passive: true });
    window.addEventListener("pointermove", onUserInput, { passive: true });
    window.addEventListener("touchstart", onUserInput, { passive: true });
    window.addEventListener("wheel", pauseAutoPlayForWheel, { passive: true });

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("pointerdown", onUserInput);
      window.removeEventListener("pointermove", onUserInput);
      window.removeEventListener("touchstart", onUserInput);
      window.removeEventListener("wheel", pauseAutoPlayForWheel);
      window.clearTimeout(resumeTimerRef.current);
    };
  }, [
    autoPlay,
    cancelProgrammaticScroll,
    chapterIndex,
    scrollToChapter,
    setAutoPlay,
    setFocusedProductIndex,
  ]);

  useEffect(() => {
    if (!autoPlay) {
      document.documentElement.classList.remove("is-loop-resetting");
      return;
    }

    if (cameraSignal.presence) {
      document.documentElement.classList.remove("is-loop-resetting");
      cancelProgrammaticScroll();
      return;
    }

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const reducedMotionTimer = window.setInterval(() => {
        const currentChapter = useExperienceStore.getState().chapterIndex;
        scrollToChapter((currentChapter + 1) % CHAPTER_COUNT);
      }, 11_000);
      return () => window.clearInterval(reducedMotionTimer);
    }

    let active = true;
    const timers = new Set<number>();
    const schedule = (callback: () => void, delay: number): void => {
      const timer = window.setTimeout(() => {
        timers.delete(timer);
        if (active) callback();
      }, delay);
      timers.add(timer);
    };

    const beginContinuousPass = (): void => {
      const remainingScreens = Math.max(
        1,
        (document.documentElement.scrollHeight - window.innerHeight - window.scrollY) /
          window.innerHeight,
      );
      scrollToEnd(Math.max(8, remainingScreens * 6.2), () => {
        if (!active) return;
        schedule(() => {
          document.documentElement.classList.add("is-loop-resetting");
          schedule(() => {
            scrollToChapter(0, true);
            schedule(() => {
              document.documentElement.classList.remove("is-loop-resetting");
              schedule(beginContinuousPass, 4_000);
            }, 450);
          }, 600);
        }, 5_500);
      });
    };

    schedule(beginContinuousPass, 3_500);

    return () => {
      active = false;
      timers.forEach((timer) => window.clearTimeout(timer));
      document.documentElement.classList.remove("is-loop-resetting");
    };
  }, [
    autoPlay,
    cameraSignal.presence,
    cancelProgrammaticScroll,
    scrollToChapter,
    scrollToEnd,
  ]);

  useEffect(() => {
    const gesture = cameraSignal.gesture;
    const previousGesture = previousGestureRef.current;
    previousGestureRef.current = gesture;

    if (gesture !== previousGesture) {
      if (gesture === "Swipe_Left") scrollToChapter(chapterIndex + 1);
      if (gesture === "Swipe_Right") scrollToChapter(chapterIndex - 1);
      if (gesture === "Open_Palm" && chapterIndex === 0) scrollToChapter(1);
    }

    const primaryHand = cameraSignal.handLandmarks[0];
    const wrist = primaryHand?.[0];
    if (wrist && (gesture === "Pinch" || gesture === "Closed_Fist")) {
      setHeroRotation({
        x: (0.5 - wrist.y) * 1.4,
        y: (0.5 - wrist.x) * 2.4,
      });
    }
    setExplosion(gesture === "Two_Hand_Spread" ? 1 : 0);
  }, [
    cameraSignal,
    chapterIndex,
    scrollToChapter,
    setExplosion,
    setHeroRotation,
  ]);
}
